// src/Views/ProtectedRoute.js
// FIXED VERSION - One-time email verification, stores status in Firestore

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { onAuthStateChanged, sendEmailVerification } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { Icon } from '@iconify/react';
import './Signup/Signup.css';

const ProtectedRoute = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [emailSent, setEmailSent] = useState(false);
    const [isVerified, setIsVerified] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                try {
                    // 1. First check Firestore for stored verification status
                    const userDocRef = doc(db, 'users', currentUser.uid);
                    const userDoc = await getDoc(userDocRef);
                    const userData = userDoc.exists() ? userDoc.data() : {};

                    // 2. If Firestore says verified, trust it (ONE-TIME CHECK)
                    if (userData.emailVerified === true) {
                        console.log('[ProtectedRoute] ✅ Email already verified (Firestore)');
                        setIsVerified(true);
                        setUser(currentUser);
                        setLoading(false);
                        return;
                    }

                    // 3. If not in Firestore, check Firebase Auth
                    await currentUser.reload();
                    const refreshedUser = auth.currentUser;

                    if (refreshedUser?.emailVerified) {
                        console.log('[ProtectedRoute] ✅ Email verified (Firebase Auth) - Saving to Firestore');
                        
                        // 4. SAVE to Firestore so we never have to check again
                        try {
                            await updateDoc(userDocRef, {
                                emailVerified: true,
                                emailVerifiedAt: new Date()
                            });
                        } catch (updateErr) {
                            console.log('[ProtectedRoute] Could not update Firestore:', updateErr);
                            // Still allow access even if Firestore update fails
                        }
                        
                        setIsVerified(true);
                        setUser(refreshedUser);
                    } else {
                        console.log('[ProtectedRoute] ❌ Email not verified');
                        setIsVerified(false);
                        setUser(refreshedUser);
                    }
                } catch (e) {
                    console.log('[ProtectedRoute] Error checking verification:', e);
                    // Fallback: trust Firebase Auth directly
                    setIsVerified(currentUser?.emailVerified || false);
                    setUser(currentUser);
                }
            } else {
                setUser(null);
                setIsVerified(false);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // 1. Show Loading while checking
    if (loading) {
        return (
            <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#0f172a', color: '#fff' }}>
                <Icon icon="eos-icons:loading" width="40" />
            </div>
        );
    }

    // 2. If not logged in, redirect to Sign In
    if (!user) {
        return <Navigate to="/signin" />;
    }

    // 3. If NOT verified (check our state, not user.emailVerified directly)
    if (!isVerified) {
        const handleResend = async () => {
            try {
                await sendEmailVerification(user);
                setEmailSent(true);
            } catch (error) {
                console.error("Resend failed", error);
                if (error.code === 'auth/too-many-requests') {
                    alert("Too many requests. Please wait a few minutes before trying again.");
                } else {
                    alert("Could not send verification email. Please try again later.");
                }
            }
        };

        const handleRefresh = async () => {
            setLoading(true);
            try {
                // Reload user from Firebase Auth
                await user.reload();
                const refreshedUser = auth.currentUser;
                
                if (refreshedUser?.emailVerified) {
                    // Update Firestore
                    const userDocRef = doc(db, 'users', refreshedUser.uid);
                    await updateDoc(userDocRef, {
                        emailVerified: true,
                        emailVerifiedAt: new Date()
                    });
                    setIsVerified(true);
                    setUser(refreshedUser);
                } else {
                    alert("Email not verified yet. Please check your inbox and click the verification link.");
                }
            } catch (e) {
                console.error("Refresh error:", e);
                alert("Could not check verification status. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        return (
            <section className="signup">
                <div className="signup__bg-grid" />
                <div className="signup__container">
                    <div className="signup__card signup__card--success">
                        <div className="signup__success-icon-wrap" style={{background: 'rgba(255, 171, 0, 0.2)', color: '#ffab00'}}>
                            <Icon icon="solar:lock-keyhole-bold" className="signup__success-icon" />
                        </div>

                        <h1 className="signup__title">Verification Required</h1>
                        <p className="signup__subtitle">
                            Access to <strong>AiPredict</strong> requires email verification. 
                            <br/>Please verify: <strong className="signup__email-highlight">{user.email}</strong>
                        </p>

                        <div className="signup__spam-box" style={{marginBottom: '20px'}}>
                            <Icon icon="solar:bell-bing-linear" className="signup__spam-icon" />
                            <div className="signup__spam-text">
                                <span className="signup__spam-title">Check your inbox</span>
                                <span>Also check <strong>Spam</strong> folder</span>
                            </div>
                        </div>

                        <div className="signup__actions">
                            <button 
                                type="button" 
                                className="signup__btn signup__btn--primary"
                                onClick={handleRefresh}
                                disabled={loading}
                            >
                                <span>{loading ? 'Checking...' : "I've Verified (Check Now)"}</span>
                                <Icon icon="solar:refresh-linear" />
                            </button>

                            {!emailSent ? (
                                <button 
                                    type="button" 
                                    className="signup__btn signup__btn--ghost"
                                    onClick={handleResend}
                                    style={{marginTop: '10px'}}
                                >
                                    <span>Resend Verification Link</span>
                                </button>
                            ) : (
                                <p style={{color: '#10b981', marginTop: '10px', fontSize: '0.9rem'}}>
                                    <Icon icon="solar:check-circle-linear" style={{verticalAlign: 'middle'}}/> Verification link sent!
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // 4. VERIFIED - Allow access to protected content
    return children;
};

export default ProtectedRoute;